import { euclideanDistance, maybeSimplifyPolygon } from '@/modules/Editor/algebra'

import type { Pt } from './geometry'
import { polylineLength, segmentIntersection, segmentsCross } from './geometry'
import { MESSAGES } from './messages'

export type Stroke = { kind: 'line' | 'lasso'; points: Pt[] }

export const TOLERANCES_PX = {
  minStroke: 5,
  close: 20,
  minLasso: 80,
  firstPoint: 12,
  simplify: 0.5,
  freehandStart: 4,
} as const

const dedupe = (points: Pt[]): Pt[] =>
  points.filter((p, i) => i === 0 || p.x !== points[i - 1].x || p.y !== points[i - 1].y)

// The ring starts at the first crossing between the stroke's head and its overshooting tail.
const trimOvershoot = (points: Pt[], closeDistance: number): Pt[] => {
  const start = points[0]
  const nearStart = (i: number): boolean =>
    euclideanDistance(points[i], start) < closeDistance ||
    euclideanDistance(points[i + 1], start) < closeDistance
  for (let i = points.length - 2; i > 1; i--) {
    if (!nearStart(i)) {
      continue
    }
    for (let j = 0; j < i - 1; j++) {
      if (!nearStart(j)) {
        continue
      }
      const crossing = segmentIntersection(points[j], points[j + 1], points[i], points[i + 1])
      if (crossing) {
        return [crossing, ...points.slice(j + 1, i + 1)]
      }
    }
  }
  return points
}

const samePoint = (a: Pt, b: Pt): boolean => a.x === b.x && a.y === b.y

// Neighbouring segments may share their joint (segmentsCross still flags a stroke that doubles
// back over itself); any other shared vertex means the stroke loops back onto itself.
const crossesItself = (points: Pt[], closed: boolean): boolean => {
  const segmentCount = points.length - (closed ? 0 : 1)
  const segment = (i: number): [Pt, Pt] => [points[i], points[(i + 1) % points.length]]
  const neighbours = (i: number, j: number): boolean =>
    j === i + 1 || (closed && i === 0 && j === segmentCount - 1)
  const boxes = Array.from({ length: segmentCount }, (_, i) => {
    const [a, b] = segment(i)
    return [Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y)]
  })
  for (let i = 0; i < segmentCount; i++) {
    for (let j = i + 1; j < segmentCount; j++) {
      // segments whose boxes do not touch cannot meet; most pairs stop here
      const [ax0, ay0, ax1, ay1] = boxes[i]
      const [bx0, by0, bx1, by1] = boxes[j]
      if (ax1 < bx0 || bx1 < ax0 || ay1 < by0 || by1 < ay0) {
        continue
      }
      const [a, b] = segment(i)
      const [c, d] = segment(j)
      const touching = [a, b].some((p) => samePoint(p, c) || samePoint(p, d))
      if ((touching && !neighbours(i, j)) || segmentsCross(a, b, c, d)) {
        return true
      }
    }
  }
  return false
}

export const resolveStroke = (input: {
  points: Pt[]
  closedByClick: boolean
  scale: number
}): Stroke | { error: string } | null => {
  const imagePx = (screenPx: number): number => screenPx / input.scale
  const raw = dedupe(input.points)
  const length = polylineLength(raw)
  if (raw.length < 2 || length < imagePx(TOLERANCES_PX.minStroke)) {
    return null
  }

  const longEnough = raw.length > 3 && length > imagePx(TOLERANCES_PX.minLasso)
  const overshoot = longEnough && !input.closedByClick
  const trimmed = overshoot ? trimOvershoot(raw, imagePx(TOLERANCES_PX.close)) : raw
  const endsNearStart =
    longEnough && euclideanDistance(raw[0], raw[raw.length - 1]) < imagePx(TOLERANCES_PX.close)
  const kind = input.closedByClick || endsNearStart || trimmed !== raw ? 'lasso' : 'line'
  const openRing = (ring: Pt[]): Pt[] =>
    kind === 'lasso' && ring.length > 1 && samePoint(ring[0], ring[ring.length - 1])
      ? ring.slice(0, -1)
      : ring
  // checked before simplifying too: simplification can straighten a stroke that retraces itself
  const unsimplified = openRing(dedupe(trimmed))
  if (crossesItself(unsimplified, kind === 'lasso')) {
    return { error: MESSAGES.selfCrossing }
  }
  const points = dedupe(maybeSimplifyPolygon(unsimplified, imagePx(TOLERANCES_PX.simplify)))
  if (kind === 'lasso' && points.length < 3) {
    return null
  }
  if (crossesItself(points, kind === 'lasso')) {
    return { error: MESSAGES.selfCrossing }
  }
  return { kind, points }
}
